import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/it';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { capitalizeFirstLetter } from './strings';
import { KeyValue } from './types';

dayjs.extend(utc);
dayjs.extend(timezone);

export const initializeDate = (timestamp: number, tenantTimezone: string) => {
  return dayjs.unix(timestamp).tz(tenantTimezone);
};

export const initializeDateTime = (
  timestamp: number,
  tenantTimezone: string,
  hours: number = 0,
  minutes: number = 0,
) => {
  const date = dayjs.unix(timestamp);
  return date
    .tz(tenantTimezone)
    .clone()
    .set('hour', hours)
    .set('minute', minutes)
    .set('second', 0)
    .set('millisecond', 0);
};

export const mergeDateAndTimeFromDifferentUnixTimestamp = (
  timestampForDate: number,
  timestampForTime: number,
  tenantTimezone: string,
) => {
  const date = dayjs.unix(timestampForDate);
  const hours = dayjs.unix(timestampForTime).tz(tenantTimezone).get('hour');
  const minutes = dayjs.unix(timestampForTime).tz(tenantTimezone).get('minute');

  return date
    .tz(tenantTimezone)
    .clone()
    .set('hour', hours)
    .set('minute', minutes)
    .set('second', 0)
    .set('millisecond', 0);
};

export const humanDate = (timestamp: number) => {
  return dayjs.unix(timestamp).format('DD/MM/YYYY HH:mm');
};

/**
 * A function that return a date with start and end time
 * @param timestamp
 * @param tenantTimezone
 * @param start should be a string formatted like this: 08:00
 * @param end same as start
 * @returns a Dayjs instance so you are free to parse that as you want
 */
export const getStartAndEndTime = (
  timestamp: number,
  tenantTimezone: string,
  start: string,
  end: string,
) => {
  const [startHour, startMinutes] = start.split(':');
  const [endHour, endMinutes] = end.split(':');

  const startTime = initializeDateTime(
    timestamp,
    tenantTimezone,
    Number(startHour),
    Number(startMinutes),
  );

  const endTime = initializeDateTime(
    timestamp,
    tenantTimezone,
    Number(endHour),
    Number(endMinutes),
  );

  return { startTime, endTime };
};

export const getStartOfFromTimestamp = (
  timestamp: number,
  tenantTimezone: string,
  unit: dayjs.OpUnitType,
) => {
  return initializeDate(timestamp, tenantTimezone).startOf(unit);
};

export const getEndOfFromTimestamp = (
  timestamp: number,
  tenantTimezone: string,
  unit: dayjs.OpUnitType,
) => {
  return initializeDate(timestamp, tenantTimezone).endOf(unit);
};

export const getLocaleFormattedTimestamp = (
  timestamp: number,
  tenantTimezone: string,
  locale: 'it' | 'en',
  format: string,
) => {
  return capitalizeFirstLetter(
    initializeDate(timestamp, tenantTimezone).locale(locale).format(format),
  );
};

export const getDaysOfWeek = (locale: string): KeyValue[] => {
  const capitalizeFirstLetter = (string: string) =>
    string.charAt(0).toUpperCase() + string.slice(1);
  dayjs.locale(locale);
  const days = [];

  // Determina il primo giorno della settimana per il locale specificato
  const startOfWeek = dayjs.utc().startOf('week');

  for (let i = 0; i < 7; i++) {
    const day = startOfWeek.add(i, 'day');
    const dayName = capitalizeFirstLetter(day.format('dddd'));
    // Calcola il valore universale del giorno a prescindere dal locale
    const dayUniversalValue = day.day();
    days.push({
      id: dayUniversalValue,
      name: `${capitalizeFirstLetter(dayName)}`,
    });
  }
  return days;
};

export const countBusinessDays = (
  start: string | Dayjs,
  end: string | Dayjs,
): number => {
  let startDate = typeof start === 'string' ? dayjs.utc(start) : start;
  const endDate = typeof end === 'string' ? dayjs.utc(end) : end;
  let count = 0;

  while (startDate.isBefore(endDate) || startDate.isSame(endDate, 'day')) {
    const dayOfWeek = startDate.day();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      // 0 = Sunday, 6 = Saturday
      count++;
    }
    startDate = startDate.add(1, 'day');
  }
  return count;
};
